import React, { useState } from 'react';
import { UserPlus, Search, Edit2, Trash2, X, Users as UsersIcon, ShieldCheck, UserCheck, ShieldOff } from 'lucide-react';
import { mockUsers, UserItem, roleLabels, statutUserLabels } from '../data/mockData';
import { toast } from 'react-toastify';
import './Users.css';

const roles: Array<UserItem['role']> = ['admin', 'agent', 'superviseur'];

const Users: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>(mockUsers);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('Tous');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showToggleConfirm, setShowToggleConfirm] = useState<string | null>(null);

  // Form state
  const [formUsername, setFormUsername] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState<UserItem['role']>('agent');
  const [formPassword, setFormPassword] = useState('');

  const filtered = users.filter(u => {
    const matchSearch = searchQuery === '' ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = filterRole === 'Tous' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.statut === 'actif').length;
  const adminCount = users.filter(u => u.role === 'admin').length;

  const resetForm = () => {
    setFormUsername('');
    setFormEmail('');
    setFormRole('agent');
    setFormPassword('');
  };

  const openCreateModal = () => {
    resetForm();
    setEditingUser(null);
    setShowCreateModal(true);
  };

  const openEditModal = (user: UserItem) => {
    setEditingUser(user);
    setFormUsername(user.username);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormPassword('');
    setShowCreateModal(true);
  };

  const handleSubmit = () => {
    if (!formUsername.trim() || !formEmail.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (editingUser) {
      // Update
      setUsers(prev => prev.map(u =>
        u.id === editingUser.id
          ? { ...u, username: formUsername, email: formEmail, role: formRole }
          : u
      ));
      toast.success(`Utilisateur "${formUsername}" modifié avec succès.`);
    } else {
      // Create
      if (!formPassword.trim()) {
        toast.error('Veuillez définir un mot de passe.');
        return;
      }
      const newUser: UserItem = {
        id: String(Date.now()),
        username: formUsername,
        email: formEmail,
        role: formRole,
        statut: 'actif',
        dateCreation: new Date().toISOString().split('T')[0],
        derniereConnexion: null,
      };
      setUsers(prev => [...prev, newUser]);
      toast.success(`Utilisateur "${formUsername}" créé avec succès.`);
    }

    setShowCreateModal(false);
    resetForm();
  };

  const handleDelete = (id: string) => {
    const user = users.find(u => u.id === id);
    setUsers(prev => prev.filter(u => u.id !== id));
    setShowDeleteConfirm(null);
    toast.success(`Utilisateur "${user?.username}" supprimé.`);
  };

  const handleRoleChange = (userId: string, newRole: UserItem['role']) => {
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, role: newRole } : u
    ));
    const user = users.find(u => u.id === userId);
    toast.success(`Rôle de "${user?.username}" changé en "${roleLabels[newRole].label}".`);
  };

  const confirmToggleStatut = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const newStatut = user.statut === 'actif' ? 'inactif' : 'actif';
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, statut: newStatut } : u
    ));
    setShowToggleConfirm(null);
    toast.success(
      newStatut === 'actif'
        ? `Compte de "${user.username}" réactivé.`
        : `Compte de "${user.username}" désactivé.`
    );
  };

  return (
    <div className="users-page">
      {/* Stats */}
      <div className="users-stats">
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-total">
            <UsersIcon size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">TOTAL UTILISATEURS</span>
            <span className="users-stat-value">{totalUsers}</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-active">
            <UserCheck size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">UTILISATEURS ACTIFS</span>
            <span className="users-stat-value">{activeUsers}</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon users-stat-icon-admin">
            <ShieldCheck size={20} />
          </div>
          <div className="users-stat-info">
            <span className="users-stat-label">ADMINISTRATEURS</span>
            <span className="users-stat-value">{adminCount}</span>
          </div>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="users-toolbar">
        <div className="users-toolbar-left">
          <div className="users-search">
            <Search size={16} className="users-search-icon" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="users-select"
          >
            <option value="Tous">Tous les rôles</option>
            {roles.map(r => (
              <option key={r} value={r}>{roleLabels[r].label}</option>
            ))}
          </select>
        </div>
        <button className="users-btn-create" onClick={openCreateModal}>
          <UserPlus size={16} /> Créer un utilisateur
        </button>
      </div>

      {/* Table */}
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Date de création</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const r = roleLabels[u.role];
              const s = statutUserLabels[u.statut];
              return (
                <tr key={u.id}>
                  <td>
                    <div className="users-cell-user">
                      <div className="users-avatar">{u.username.charAt(0).toUpperCase()}</div>
                      <span className="users-username">{u.username}</span>
                    </div>
                  </td>
                  <td className="users-email">{u.email}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value as UserItem['role'])}
                      className="users-role-select"
                      style={{ color: r.color, background: r.bg }}
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>{roleLabels[role].label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="users-toggle-wrap">
                      <button
                        className={`users-toggle ${u.statut === 'actif' ? 'users-toggle-on' : 'users-toggle-off'}`}
                        onClick={() => setShowToggleConfirm(u.id)}
                        title={u.statut === 'actif' ? 'Désactiver' : 'Réactiver'}
                      >
                        <span className="users-toggle-knob" />
                      </button>
                      <span className="users-toggle-label" style={{ color: s.color }}>{s.label}</span>
                    </div>
                  </td>
                  <td className="users-date">{new Date(u.dateCreation).toLocaleDateString('fr-FR')}</td>
                  <td className="users-date">
                    {u.derniereConnexion
                      ? new Date(u.derniereConnexion).toLocaleDateString('fr-FR')
                      : <span className="users-never">Jamais</span>
                    }
                  </td>
                  <td>
                    <div className="users-actions">
                      <button className="users-action-btn users-action-edit" onClick={() => openEditModal(u)} title="Modifier">
                        <Edit2 size={14} />
                      </button>
                      <button className="users-action-btn users-action-delete" onClick={() => setShowDeleteConfirm(u.id)} title="Supprimer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="users-empty">Aucun utilisateur ne correspond aux critères.</div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="users-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="users-modal" onClick={e => e.stopPropagation()}>
            <div className="users-modal-head">
              <div className="users-modal-title-row">
                <div className="users-modal-icon">
                  <UserPlus size={20} />
                </div>
                <div>
                  <h3 className="users-modal-title">
                    {editingUser ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
                  </h3>
                  <p className="users-modal-subtitle">
                    {editingUser ? 'Modifiez les informations du compte.' : 'Remplissez les informations du nouveau compte.'}
                  </p>
                </div>
              </div>
              <button className="users-modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="users-modal-body">
              <div className="users-form-group">
                <label>Nom d'utilisateur *</label>
                <input
                  type="text"
                  placeholder="ex: j.kouassi"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                />
              </div>
              <div className="users-form-group">
                <label>Email *</label>
                <input
                  type="email"
                  placeholder="ex: j.kouassi@cndj.ci"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
              </div>
              <div className="users-form-group">
                <label>Rôle *</label>
                <select value={formRole} onChange={(e) => setFormRole(e.target.value as UserItem['role'])}>
                  {roles.map(r => (
                    <option key={r} value={r}>{roleLabels[r].label}</option>
                  ))}
                </select>
              </div>
              {!editingUser && (
                <div className="users-form-group">
                  <label>Mot de passe *</label>
                  <input
                    type="password"
                    placeholder="Mot de passe temporaire"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="users-modal-foot">
              <button className="users-btn-cancel" onClick={() => setShowCreateModal(false)}>
                Annuler
              </button>
              <button className="users-btn-submit" onClick={handleSubmit}>
                {editingUser ? 'Enregistrer' : 'Créer le compte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="users-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="users-modal users-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="users-modal-head">
              <div className="users-modal-title-row">
                <div className="users-modal-icon users-modal-icon-danger">
                  <Trash2 size={20} />
                </div>
                <div>
                  <h3 className="users-modal-title">Supprimer l'utilisateur</h3>
                  <p className="users-modal-subtitle">
                    Êtes-vous sûr de vouloir supprimer « {users.find(u => u.id === showDeleteConfirm)?.username} » ? Cette action est irréversible.
                  </p>
                </div>
              </div>
              <button className="users-modal-close" onClick={() => setShowDeleteConfirm(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="users-modal-foot">
              <button className="users-btn-cancel" onClick={() => setShowDeleteConfirm(null)}>
                Annuler
              </button>
              <button className="users-btn-danger" onClick={() => handleDelete(showDeleteConfirm)}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle statut confirmation modal */}
      {showToggleConfirm && (() => {
        const targetUser = users.find(u => u.id === showToggleConfirm);
        const isActivating = targetUser?.statut === 'inactif';
        return (
          <div className="users-modal-overlay" onClick={() => setShowToggleConfirm(null)}>
            <div className="users-modal users-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="users-modal-head">
                <div className="users-modal-title-row">
                  <div className={`users-modal-icon ${isActivating ? '' : 'users-modal-icon-danger'}`}>
                    <ShieldOff size={20} />
                  </div>
                  <div>
                    <h3 className="users-modal-title">
                      {isActivating ? 'Réactiver le compte' : 'Désactiver le compte'}
                    </h3>
                    <p className="users-modal-subtitle">
                      {isActivating
                        ? `Voulez-vous réactiver le compte de « ${targetUser?.username} » ? L'utilisateur pourra se connecter à nouveau.`
                        : `Voulez-vous désactiver le compte de « ${targetUser?.username} » ? L'utilisateur ne pourra plus se connecter.`
                      }
                    </p>
                  </div>
                </div>
                <button className="users-modal-close" onClick={() => setShowToggleConfirm(null)}>
                  <X size={18} />
                </button>
              </div>
              <div className="users-modal-foot">
                <button className="users-btn-cancel" onClick={() => setShowToggleConfirm(null)}>
                  Annuler
                </button>
                <button
                  className={isActivating ? 'users-btn-submit' : 'users-btn-danger'}
                  onClick={() => confirmToggleStatut(showToggleConfirm)}
                >
                  {isActivating ? 'Réactiver' : 'Désactiver'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Users;
